<template>
    <div class="menu-manager-container">
        <!-- 操作按钮 -->
        <div class="page-header">
            <div class="header-actions">
                <el-button
                        type="success"
                        icon="Check"
                        @click="submitAllChanges"
                        :disabled="!hasChanges"
                        class="save-all-btn"
                >
                    保存所有修改
                </el-button>
                <el-button
                        type="warning"
                        icon="RefreshLeft"
                        @click="resetChanges"
                        :disabled="!hasChanges"
                        class="reset-btn"
                >
                    撤销修改
                </el-button>
                <el-button
                        type="primary"
                        icon="Plus"
                        @click="openAddMainMenuModal"
                        class="add-main-btn"
                >
                    添加主菜单
                </el-button>
            </div>
        </div>

        <!-- 加载中提示 -->
        <div v-if="loading" class="loading-container">
            <el-skeleton :loading="loading" animated>
                <div style="height: 200px;"></div>
            </el-skeleton>
        </div>

        <!-- 空列表提示 -->
        <div v-else-if="!menuList.length" class="empty-container">
            <el-empty description="暂无菜单数据，点击“添加主菜单”创建吧">
                <el-button type="primary" @click="openAddMainMenuModal">
                    添加主菜单
                </el-button>
            </el-empty>
        </div>

        <!-- 菜单列表：引入菜单页递归 + 顶级菜单拖拽初始化 -->
        <div v-else class="menu-list" ref="mainMenuListRef" data-menu-type="1">
            <EditMenuModel
                    v-for="menu in menuList"
                    :key="menu.menuId"
                    :menu="menu"
                    :checkMenuModified="menu.menuType == '1' && menu.children?.length > 0"
                    :openAddSubMenuModal="openAddSubMenuModal"
                    :editMenu="openMenuModal"
                    :markMenuDelete="markMenuDelete"
                    :handleMenuDrag="handleMenuDrag"
            />
        </div>

        <!-- Element Plus弹窗 -->
        <el-dialog
                v-model="showModal"
                :title="modalTitle"
                width="600px"
                height="470px"
                :close-on-click-modal="false"
                :close-on-press-escape="true"
                :closeable="true"
                @close="handleCancelModal"
        >
            <!-- 菜单表单组件 -->
            <MenuModal
                    ref="menuModalRef"
                    :formData="formData"
                    @update:formData="(val) => formData = val"
            />

            <!-- 自定义弹窗底部按钮（居中）-->
            <template #footer>
                <div class="dialog-footer" style="text-align: center; width: 100%;">
                    <el-button @click="handleCancelModal">返回</el-button>
                    <el-button
                            type="primary"
                            @click="handleSave"
                            :disabled="!isFormValid"
                    >
                        保存
                    </el-button>
                </div>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
    import { setupEditMenu } from './editMenu.js';
    import EditMenuModel from './EditMenuModel.vue';
    import MenuModal from './MenuModal.vue';
    import { ref, computed, watch, nextTick ,onUnmounted} from 'vue';
    import { ElButton, ElEmpty, ElSkeleton, ElMessage } from 'element-plus';
    import Sortable from 'sortablejs'; // 引入Sortable

    const {
        menuList,
        sortablesType,
        loading,
        showModal,
        isEdit,
        formData,
        hasChanges,
        openAddMainMenuModal,
        openAddSubMenuModal,
        openMenuModal,
        closeModal,
        saveMenuToLocal,
        markMenuDelete,
        submitAllChanges,
        resetChanges,
        parentMenuId,
        handleMenuDrag // 引入拖拽处理方法
    } = setupEditMenu();

    // ========== 拖拽板块 ==========
    // 顶级菜单列表ref
    const mainMenuListRef = ref(null);
    let mainSortableInstance = null;
    //  初始化Sortable（顶层菜单的拖拽渲染 关联 mainMenuListRef）
    const initMainSortable = () => {
        // 先销毁
        if (mainSortableInstance) mainSortableInstance.destroy();
        //页面未渲染完毕 返回
        const container = mainMenuListRef.value;
        if (!container || !container.querySelector('.menu-item-wrapper')) return;

        // 3.再初始化菜单拖拽组件
        mainSortableInstance = new Sortable(mainMenuListRef.value, {
            animation: 150,
            ghostClass: 'sortable-ghost',
            handle: '.menu-header',
            filter: '.menu-actions',
            draggable: '.menu-item-wrapper',
            group: {
                name: 'menu-group', // 统一拖拽组名，支持跨层级
                pull: true,      // 允许拖出
                put: true           // 允许拖入
            },
            // 拖拽过程中的校验 可阻止拖拽
            onMove: (evt) => {
                // 获取目标父菜单类型
                const newmenuType = evt.to?.dataset.menuType //对应 data-menu-type
                // 顶级菜单容器无data-menu-id，允许拖拽（顶级默认是文件夹）
                if (!newmenuType) return false;
                // 校验：目标父菜单必须是文件夹（menuType=1）
                if (newmenuType !== '1'){
                    return false;
                }
                return true; // 允许拖拽
            },
            onEnd: (evt) => {
                const menuId =  menuList.value[evt.oldIndex].menuId;// 被拖拽的菜单ID
                const oldParentMenuId = null; //当前容器的菜单ID（文件夹ID）
                //目标菜单ID（当前文件夹ID）需要给每个子容器设置data-menu-id才被能取到
                //顶层菜单 不设置 data-menu-id 如果目标容器是顶层菜单 ，取不到 则是 null
                const newParentMenuId = evt.to?.dataset.menuId || null;
                const newIndex = evt.newIndex; // 新索引

                handleMenuDrag({ menuId, oldParentMenuId,newParentMenuId, newIndex });
            }
        });
    };

    // 监听menuList变化，菜单查询后 menuList 有值 再渲染拖拽组件
    watch(
        () => sortablesType.value, // 监听sortablesType的响应式变化
        (newVal) => {
            // 当sortablesType变为true时执行初始化
            if (newVal) {
                // nextTick确保DOM渲染完成后再初始化Sortable
                nextTick(() => {
                    initMainSortable();
                });
            }
        }
    );
    // 页面销毁时销毁实例（避免内存泄漏）
    onUnmounted(() => {
        if (mainSortableInstance) {
            mainSortableInstance.destroy();
            mainSortableInstance = null;
        }
    });
    // ========== 拖拽板块结束 ==========

    // 表单Ref和验证逻辑
    const menuModalRef = ref(null);

    // 弹窗标题计算逻辑
    const modalTitle = computed(() => {
        if (isEdit.value) return '编辑菜单';
        return parentMenuId.value ? '添加子菜单' : '添加主菜单';
    });

    // 表单验证逻辑
    const isFormValid = computed(() => {
        if (!menuModalRef.value) return false;
        const validateResult = menuModalRef.value.validateForm();
        return validateResult.valid;
    });

    // 表单监听逻辑
    watch(
        [() => formData.menuName, () => formData.menuType, () => formData.menuUrl],
        () => {},
        { deep: true }
    );

    // 弹窗取消逻辑
    const handleCancelModal = () => {
        closeModal();
    };

    // 保存逻辑（逻辑保存，更新 menuList ）
    const handleSave = () => {
        if (!menuModalRef.value) return;
        const validateResult = menuModalRef.value.validateForm();
        if (!validateResult.valid) {
            ElMessage.warning(validateResult.message);
            return;
        }
        saveMenuToLocal();
    };
</script>

<style scoped>
    /*scoped 引入的css 会有隔离*/
    @import '../../../assets/css/editMenu.css';

    /* ========== 拖拽板块 ========== */
    :deep(.sortable-ghost) {
        opacity: 0.8;
        background: #f5f7fa;
        border-radius: 4px;
    }
    /* ========== 拖拽板块结束 ========== */
</style>